import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetHomepageSettingsQuery,
  useSubmitContactFormMutation,
} from "../redux/services/api";
import { setSettings } from "../redux/slices/homepageSlice";
import { selectSettings } from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "./StatusMessage";
import AOS from "aos";
import "aos/dist/aos.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faLinkedin,
  faInstagram,
  faYoutube,
  faTiktok,
  faSnapchat,
  faPinterest,
  faTelegram,
  faDiscord,
} from "@fortawesome/free-brands-svg-icons";

const ContactUs = () => {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const { data: settingsData } = useGetHomepageSettingsQuery();
  const { t, isArabic } = useTranslation();

  // Use RTK Query mutation for contact form submission
  const [submitContactForm, { isLoading: isSubmitting }] =
    useSubmitContactFormMutation();

  useEffect(() => {
    if (settingsData && settingsData.success) {
      dispatch(setSettings(settingsData.settings));
    }
  }, [settingsData, dispatch]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const result = await submitContactForm(formData).unwrap();

      if (result.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      setSubmitStatus("error");
    }
  };

  // Helper function to format WhatsApp link
  const formatWhatsAppLink = (whatsappValue, defaultMessage = "") => {
    if (!whatsappValue) return "";

    let baseUrl = "";
    let phoneNumber = "";

    // If it's already a URL, extract phone number (ignore any existing text parameter)
    if (
      whatsappValue.startsWith("https://wa.me/") ||
      whatsappValue.startsWith("https://api.whatsapp.com/")
    ) {
      // Extract phone number from URL (remove any existing parameters)
      const urlMatch = whatsappValue.match(/(?:wa\.me\/|api\.whatsapp\.com\/send\?phone=)([^\/\?&]+)/);
      if (urlMatch && urlMatch[1]) {
        phoneNumber = urlMatch[1].replace(/[^\d+]/g, "");
      } else {
        // If we can't extract, return as is but add message if needed
        if (defaultMessage && defaultMessage.trim()) {
          const separator = whatsappValue.includes("?") ? "&" : "?";
          const encodedMessage = encodeURIComponent(defaultMessage.trim());
          return `${whatsappValue}${separator}text=${encodedMessage}`;
        }
        return whatsappValue; // Return as is if we can't parse it
      }
    } else if (whatsappValue.startsWith("whatsapp://")) {
      // For whatsapp:// protocol, we can't easily add text, so return as is
      return whatsappValue;
    } else {
      // If it's just a phone number, extract it
      phoneNumber = whatsappValue.replace(/[^\d+]/g, "");
    }

    // Remove leading + if present (wa.me format doesn't need it)
    if (phoneNumber.startsWith("+")) {
      phoneNumber = phoneNumber.substring(1);
    }

    // Build base URL
    baseUrl = `https://wa.me/${phoneNumber}`;

    // Add default message if provided
    if (defaultMessage && defaultMessage.trim()) {
      const encodedMessage = encodeURIComponent(defaultMessage.trim());
      baseUrl += `?text=${encodedMessage}`;
    }

    return baseUrl;
  };

  // Get contact information from settings or use defaults
  const contactInfo = {
    phone: settings?.contact?.phone || "+971 4 123 4567",
    email: settings?.contact?.email || "info@silverfort.com",
    address: settings?.contact?.address || "",
    addressAr: settings?.contact?.addressAr || "",
    addressLink: settings?.contact?.addressLink || "",
    whatsapp: settings?.contact?.whatsapp || "",
    whatsappDefaultMessage: settings?.contact?.whatsappDefaultMessage || "",
    whatsappLink: formatWhatsAppLink(
      settings?.contact?.whatsapp || "",
      settings?.contact?.whatsappDefaultMessage || ""
    ),
    socialMedia: settings?.socialMedia || {},
  };

  // Custom X (formerly Twitter) icon component
  const XIcon = ({ className = "" }) => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      width="1em"
      height="1em"
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );

  // Define social media platforms with their icons
  const socialMediaPlatforms = [
    { key: "facebook", icon: faFacebook, label: "Facebook" },
    { key: "twitter", icon: "x", label: "X (Twitter)", customIcon: true },
    { key: "linkedin", icon: faLinkedin, label: "LinkedIn" },
    { key: "instagram", icon: faInstagram, label: "Instagram" },
    { key: "youtube", icon: faYoutube, label: "YouTube" },
    { key: "tiktok", icon: faTiktok, label: "TikTok" },
    { key: "snapchat", icon: faSnapchat, label: "Snapchat" },
    { key: "pinterest", icon: faPinterest, label: "Pinterest" },
    { key: "telegram", icon: faTelegram, label: "Telegram" },
    { key: "discord", icon: faDiscord, label: "Discord" },
  ];

  // Filter social media platforms that have URLs
  const availableSocialMedia = socialMediaPlatforms.filter(
    (platform) =>
      contactInfo.socialMedia[platform.key] &&
      contactInfo.socialMedia[platform.key].trim() !== ""
  );

  return (
    <section className="contact-us" id="contact">
      <div className="container">
        <div data-aos="fade-up" data-aos-delay="100">
          <h2 className="section-title">{t("contact.title")}</h2>
          <p className="section-subtitle">{t("contact.subtitle")}</p>
        </div>

        <div className="contact-content">
          {/* Contact Information */}
          <div
            className="contact-info"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            <a
              href={`tel:${contactInfo.phone}`}
              className="contact-item"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <div className="contact-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.phone")}</h3>
                <p style={{ direction: isArabic ? "ltr" : "inherit" }}>
                  {contactInfo.phone}
                </p>
              </div>
            </a>

            <a
              href={`mailto:${contactInfo.email}`}
              className="contact-item"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <div className="contact-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.email")}</h3>
                <p>{contactInfo.email}</p>
              </div>
            </a>

            {(contactInfo.address || contactInfo.addressAr) && (
              <a
                href={
                  contactInfo.addressLink ||
                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    isArabic && contactInfo.addressAr
                      ? contactInfo.addressAr
                      : contactInfo.address
                  )}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "flex",
                  cursor: "pointer",
                }}
              >
              <div className="contact-icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.address")}</h3>
                <p>
                  {isArabic && contactInfo.addressAr
                    ? contactInfo.addressAr
                    : contactInfo.address}
                </p>
              </div>
              </a>
            )}

            {contactInfo.whatsapp && (
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "flex",
                  cursor: "pointer",
                }}
              >
                <div className="contact-icon">
                  <FontAwesomeIcon icon={faWhatsapp} />
                </div>
                <div className="contact-details">
                  <h3>WhatsApp</h3>
                  <p style={{ direction: isArabic ? "ltr" : "inherit" }}>
                    {contactInfo.whatsapp}
                  </p>
                </div>
              </a>
            )}

            {availableSocialMedia.length > 0 && (
            <div className="contact-item">
              <div className="contact-icon">
                  <FontAwesomeIcon icon={faFacebook} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.followUs")}</h3>
                <div className="social-media">
                    {availableSocialMedia.map((platform) => (
                  <a
                        key={platform.key}
                        href={contactInfo.socialMedia[platform.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                        aria-label={platform.label}
                      >
                        {platform.customIcon ? (
                          <XIcon />
                        ) : (
                          <FontAwesomeIcon icon={platform.icon} />
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div
            className="contact-form"
            data-aos="fade-right"
            data-aos-delay="300"
          >
            <h3 className="form-title">{t("contact.title")}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t("contact.name")}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={t("contact.placeholders.name")}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t("contact.email")}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={t("contact.placeholders.email")}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">{t("contact.phone")}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t("contact.placeholders.phone")}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">{t("contact.subject")}</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder={t("contact.placeholders.subject")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">{t("contact.message")}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  placeholder={t("contact.placeholders.message")}
                ></textarea>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("common.loading") : t("contact.send")}
              </button>

              <StatusMessage
                message={
                  submitStatus === "success"
                    ? "Thank you! Your message has been sent successfully."
                    : submitStatus === "error"
                    ? "Sorry, there was an error sending your message. Please try again."
                    : null
                }
                type={
                  submitStatus === "success"
                    ? "success"
                    : submitStatus === "error"
                    ? "error"
                    : "info"
                }
                show={!!submitStatus}
                autoClose={submitStatus === "success"}
                autoCloseDelay={5000}
              />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
